import React from 'react';
import {
  ArrowRight,
  PlayCircle,
  CheckCircle2,
  Clock,
  Truck,
} from 'lucide-react';

const CARRIER_QUOTES = [
  { name: 'Atlas Freight Co.', price: 2480, eta: '2d 4h', status: 'beaten' },
  { name: 'Northwind Logistics', price: 2390, eta: '2d 1h', status: 'beaten' },
  { name: 'Pioneer Carriers', price: 2295, eta: '1d 22h', status: 'winner' },
  { name: 'Skyline Transport', price: 2360, eta: '2d 0h', status: 'beaten' },
];

export default function Hero() {
  return (
    <section id="top" className="relative overflow-hidden bg-white">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-[520px] bg-radial-fade pointer-events-none"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-grid-faint [background-size:48px_48px] opacity-[0.35] mask-fade-y pointer-events-none"
      />

      <div className="container-page relative pt-16 pb-20 lg:pt-12 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <h1 className="mt-5 h-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-balance">
              Your freight desk, on autopilot.
              <br />
              <span className="text-gradient-ink">
                Your savings, on record.
              </span>
            </h1>

            <p className="mt-6 text-lg text-ink-600 max-w-xl text-balance">
              Vibe Engine runs your tender workflows end to end — collecting
              bids, comparing them to live market rates, and awarding the best
              qualified carrier without manual phone tag. Your team stays in
              control; the platform does the repetitive work.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="#demo" className="btn-primary">
                Book a demo
                <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#how-it-works" className="btn-secondary">
                <PlayCircle className="h-4 w-4" />
                See how it works
              </a>
            </div>

            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-600">
              <li className="inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-ink-900 shrink-0" />
                Real-time market rate analysis vs. incoming carrier bids
              </li>
              <li className="inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-ink-900 shrink-0" />
                No carrier onboarding headache
              </li>
            </ul>
          </div>

          <div className="lg:col-span-5">
            <HeroVisual />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroVisual() {
  return (
    <div className="relative animate-slide-up">
      <div
        aria-hidden="true"
        className="absolute -inset-6 rounded-3xl bg-ink-100/60 blur-2xl"
      />
      <div className="relative card-surface p-5 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-ink-100 text-ink-900">
              <Truck className="h-4 w-4" />
            </span>
            <div>
              <div className="text-sm font-semibold text-ink-900">
                Load #VBE-48219
              </div>
              <div className="text-xs text-ink-500">
                Dallas, TX &rarr; Atlanta, GA · Dry Van · 42,000 lbs
              </div>
            </div>
          </div>
          <span className="pill">
            <Clock className="h-3 w-3" /> Wait for All
          </span>
        </div>

        <div className="mt-5 flex items-center justify-between rounded-xl border border-ink-200 bg-ink-50 px-3.5 py-2.5">
          <span className="text-xs font-medium text-ink-600">
            Current market rate
          </span>
          <span className="text-sm font-semibold text-ink-900 tabular-nums">
            $2,550
          </span>
        </div>

        <div className="mt-3 space-y-2.5">
          {CARRIER_QUOTES.map((q) => {
            const isWinner = q.status === 'winner';
            return (
              <div
                key={q.name}
                className={`flex items-center justify-between rounded-xl border px-3.5 py-3 transition-colors ${
                  isWinner
                    ? 'border-ink-900 bg-ink-900 text-white'
                    : 'border-ink-200 bg-white hover:bg-ink-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-xs font-semibold ${
                      isWinner ? 'bg-white/15 text-white' : 'bg-ink-100 text-ink-700'
                    }`}
                  >
                    {q.name
                      .split(' ')
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join('')}
                  </span>
                  <div>
                    <div
                      className={`text-sm font-medium ${
                        isWinner ? 'text-white' : 'text-ink-900'
                      }`}
                    >
                      {q.name}
                    </div>
                    <div
                      className={`text-xs ${
                        isWinner ? 'text-white/70' : 'text-ink-500'
                      }`}
                    >
                      ETA {q.eta}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-sm tabular-nums font-semibold ${
                      isWinner ? 'text-white' : 'text-ink-700'
                    }`}
                  >
                    ${q.price.toLocaleString()}
                  </span>
                  {isWinner ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-white text-ink-900 rounded-full px-2 py-0.5">
                      <CheckCircle2 className="h-3 w-3" />
                      Winner
                    </span>
                  ) : (
                    <span className="text-[11px] text-ink-400">·</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 rounded-xl border border-ink-200 bg-ink-50 px-4 py-3 flex items-center justify-between">
          <div className="text-xs text-ink-600">
            Saved vs. first-yes carrier
          </div>
          <div className="text-sm font-semibold text-ink-900 tabular-nums">
            -$185 · 7.5%
          </div>
        </div>
      </div>
    </div>
  );
}
